import React, { useRef } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Box, Button, Typography } from '@heycater/design-system';
import { ButtonBase, Divider, Grid } from '@material-ui/core';
import { ArrowForwardIos } from '@material-ui/icons';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import ArticleCard from 'hub/components/ArticleCard';
import SignUpForm from 'hub/components/ArticlePage/SignupForm';
import ContentfulImage from 'hub/components/ContentfulImage';
import ProgressBar from 'hub/components/ProgressBar';
import SocialButtons from 'hub/components/SocialButtons';
import TableOfContents, {
  anchorableHeaderOptions,
  MappedHeadings,
} from 'hub/components/TableOfContents';
import {
  embeddedAssetOptions,
  getContentfulImageUrl,
} from 'hub/helpers/contentful';
import { Tag } from 'hub/styles';
import { ParsedArticle } from 'lib/contentful/shapes';
import { useBreakpointDown } from 'qualification/hooks/useBreakpoints';
import Dots from 'shared/components/Dots';
import FlexBox from 'shared/components/FlexBox';
import { formatDate } from 'shared/helpers/dates';
import { usePagination } from 'shared/hooks/usePagination';
import MainLayout from 'shared/layouts/MainLayout';
import { Ratings } from 'shared/services/trustedShops/model';
import FooterLinks from 'static_pages/components/FooterLinks';
import { AreaWithSidebarCTA } from 'static_pages/marketing_pages/components/SidebarCTA';
import Container from 'system/components/Container';

const POST_SUGGESTIONS_PER_SLIDE = 4;

export interface ArticlePageProps {
  post: ParsedArticle;
  headings: MappedHeadings;
  ratings: Ratings;
}

function ArticlePage({ post, headings, ratings }: ArticlePageProps) {
  const { t } = useTranslation('hub');

  const assetOptions = embeddedAssetOptions();
  const isMobile = useBreakpointDown('sm');
  const body = documentToReactComponents(post.body, {
    ...assetOptions,
    renderNode: {
      ...assetOptions.renderNode,
      ...anchorableHeaderOptions.renderNode,
    },
  });

  const { locale } = useRouter();
  const contentRef = useRef<null | HTMLDivElement>(null);
  const { targetAudience = 'customer' } = post;
  const socialMediaUrl = React.useMemo(
    () =>
      `${process.env.NEXT_PUBLIC_HOST}/hub/${post.postCategory.slug}/${post.slug}`,
    [post]
  );

  const postSuggestionSlides = usePagination(post.suggestedArticles, {
    pageSize: POST_SUGGESTIONS_PER_SLIDE,
  });

  const hasPostSuggestionSlider =
    !isMobile && postSuggestionSlides.pageCount > 1;

  return (
    <MainLayout ratings={ratings}>
      <Head>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <meta property="og:title" content={`${post.title} | heycater!`} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta
          property="og:image"
          content={`${getContentfulImageUrl(
            post.heroImage
          )}?w=1200&h=630&fit=fill`}
        />
      </Head>
      <ProgressBar contentRef={contentRef} />
      <div ref={contentRef}>
        <Box mt={isMobile ? 0 : 4}>
          <Container disableGutters={isMobile} size="sm">
            <Box borderRadius={isMobile ? undefined : '8px'} overflow="hidden">
              <ContentfulImage
                src={post.heroImage}
                priority
                quality={90}
                width={890}
                height={480}
                layout="responsive"
              />
            </Box>
          </Container>
          <Container size="xs">
            <AreaWithSidebarCTA
              title={post.buttonCta}
              subtitle={
                post.buttonCtaSubtitle
                  ? post.buttonCtaSubtitle
                  : t('post.buttonSubtitle')
              }
              ctaText={post.buttonCtaCopy || t('post.footerCTAButtonLabel')}
              url={post.buttonUrl}
            >
              <Box display="flex" mt={2} justifyContent="space-between">
                <Typography
                  variant="caption"
                  color="textSecondary"
                  component="p"
                >
                  {t('post.categoryLabel')}:{' '}
                  <Link passHref href={`/hub/${post.postCategory.slug}`}>
                    <Anchor>{post.postCategory.category}</Anchor>
                  </Link>
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  component="p"
                >
                  {formatDate(post.updatedAt, locale)}
                </Typography>
              </Box>

              <Box mt={2}>
                <Typography
                  variant="h3"
                  fontSize={isMobile ? '28px' : '40px'}
                  component="h1"
                >
                  {post.title}
                </Typography>
              </Box>
              {post.author && (
                <FlexBox mt={3} mb={2} alignItems="center" gap={3}>
                  <Box
                    position="relative"
                    borderRadius="500px"
                    overflow="hidden"
                    width="65px"
                  >
                    <Image
                      src={`https:${post.author.avatar.file.url}`}
                      width={post.author.avatar.file.details.image?.width}
                      height={post.author.avatar.file.details.image?.height}
                      layout="responsive"
                      objectFit="cover"
                      alt=""
                    />
                  </Box>
                  <FlexBox flexDirection="column">
                    <Typography fontWeight="500">
                      {t(`post.author.${isMobile ? 'brief' : 'verbose'}`, {
                        author: post.author.name,
                      })}
                    </Typography>
                    {post.author?.description && (
                      <TypographyAuthorDescription fontWeight="500">
                        {post.author.description}
                      </TypographyAuthorDescription>
                    )}
                  </FlexBox>
                </FlexBox>
              )}

              <FlexBox gap={isMobile ? 2 : 5} flexDirection={'row'} mt={4}>
                <TableOfContents mb={isMobile ? 1.2 : 4} headings={headings} />

                <SocialButtons
                  direction={'column'}
                  socialMediaUrl={socialMediaUrl}
                  title={post.title}
                />
              </FlexBox>
              <Box mb={6}>
                <Box mb={4}>
                  <Typography variant="body1">{body}</Typography>
                </Box>
                <Box>
                  {post.tags.map((tag) => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </Box>
              </Box>
              <Divider />
              <Box mb={{ xs: 3, sm: 2 }} mt={{ xs: 2, sm: 3 }} py={3}>
                <SignUpForm
                  title={
                    post?.contactForm?.fields?.title ||
                    t(
                      `signUpForm.blogPosts.${targetAudience.toLowerCase()}Messages.title`
                    )
                  }
                  subTitle={
                    post.contactForm?.fields?.subtitle ||
                    t(
                      `signUpForm.blogPosts.${targetAudience.toLowerCase()}Messages.subTitle`
                    )
                  }
                  imageSrc={
                    post.contactForm?.fields?.image
                      ? getContentfulImageUrl(post.contactForm.fields.image)
                      : undefined
                  }
                  successFeedback={post?.contactForm?.fields?.successFeedback}
                  buttonText={post?.contactForm?.fields?.buttonText}
                  contactListId={post.contactForm?.fields.contactList.fields.id}
                />
              </Box>
              <Box mb={8} mt={4}>
                <Divider />
              </Box>
            </AreaWithSidebarCTA>
          </Container>
        </Box>
      </div>

      {post.buttonCta.length > 0 && post.buttonUrl.length > 0 && (
        <FooterCTA>
          <Container size="sm">
            <Box mb={4}>
              <Typography
                variant="h4"
                component="h2"
                align="center"
                gutterBottom
              >
                {post.buttonCta}
              </Typography>
            </Box>
            <Button
              rounded
              size="lg"
              component="a"
              passHref
              href={post.buttonUrl}
              target="_blank"
              rel="noopener"
              id="hub-footer-cta"
            >
              {post.buttonCtaCopy || t('post.footerCTAButtonLabel')}
            </Button>
          </Container>
        </FooterCTA>
      )}
      {postSuggestionSlides.currentPage?.length > 0 && (
        <SuggestedArticlesWrapper as="section">
          <Container size="lg">
            <Box pb={6} textAlign="center">
              <Typography variant="h3" component="h2" gutterBottom>
                {t('post.suggestedArticlesLabel')}
              </Typography>
            </Box>
            <FlexBox>
              {hasPostSuggestionSlider && (
                <FlexBox alignItems="center" mr={5} ml={1}>
                  <SlideButton onClick={postSuggestionSlides.prev}>
                    <ArrowBack fontSize="large" />
                  </SlideButton>
                </FlexBox>
              )}
              <ListWrapper>
                <Grid component="ul" container spacing={3}>
                  {postSuggestionSlides.currentPage.map((suggestedArticle) => (
                    <Grid
                      component="li"
                      item
                      xs={12}
                      sm={12}
                      md={6}
                      key={suggestedArticle.slug}
                    >
                      <ArticleCard {...suggestedArticle} />
                    </Grid>
                  ))}
                </Grid>
              </ListWrapper>

              {hasPostSuggestionSlider && (
                <FlexBox alignItems="center" ml={5} mr={1}>
                  <Box ml={5}>
                    <SlideButton onClick={postSuggestionSlides.next}>
                      <ArrowForwardIos fontSize="large" />
                    </SlideButton>
                  </Box>
                </FlexBox>
              )}
            </FlexBox>
            {hasPostSuggestionSlider && (
              <Box
                display="flex"
                justifyContent="center"
                width="100%"
                mt={3}
                mb={1}
              >
                <Dots
                  count={postSuggestionSlides.pageCount}
                  activeIndex={postSuggestionSlides.pageIndex}
                  onClick={postSuggestionSlides.setPageIndex}
                />
              </Box>
            )}
            <Box mt={{ xs: 4, md: 5 }} textAlign={'center'}>
              <Link href="/hub" passHref>
                <ButtonLink variant="tertiary" component="a">
                  {t('post.otherArticles')}
                </ButtonLink>
              </Link>
            </Box>
          </Container>
        </SuggestedArticlesWrapper>
      )}
      <FooterLinks pb={{ xs: 2, sm: 6 }} pt={{ xs: 2, sm: 3 }} />
    </MainLayout>
  );
}

/**
 * STYLED COMPONENTS
 */

const ListWrapper = styled.div`
  padding: 0;
  margin: 0;
  ul {
    padding-left: 0;
    list-style: none;

    li {
      height: 100%;
    }
  }
`;

const ArrowBack = styled(ArrowForwardIos)`
  transform: scaleX(-1);
`;

const SlideButton = styled(ButtonBase)`
  transition: background-color 0.2s ease-out;
  height: 42px;
  width: 42px;
  border-radius: 4px;
  padding: ${({ theme }) => theme.spacing(4, 0)};
  :hover {
    background-color: ${({ theme }) => theme.palette.grey[200]};
  }
  :active {
    background-color: ${({ theme }) => theme.palette.grey[300]};
  }
`;

const Anchor = styled.a`
  color: inherit;
`;

const ButtonLink = styled(Button)`
  text-transform: inherit;
  padding: ${(props) => props.theme.spacing(1, 2)};
  font-weight: 500;
  line-height: ${({ theme }) => theme.spacing(3)}px;
  letter-spacing: 0.15px;
  font-size: 14px;
`;

const TypographyAuthorDescription = styled(Typography)`
  color: ${({ theme }) => theme.palette.secondary['400']};
`;

const FooterCTA = styled(Box)`
  padding: ${(props) => props.theme.spacing(8, 0)};
  background: ${(props) => props.theme.colors.heroGradient};
  text-align: center;
  border-radius: 8px;
  width: 100%;
`;

const SuggestedArticlesWrapper = styled.div`
  padding: ${(props) => props.theme.spacing(8, 0)};
`;

export default ArticlePage;
